<script setup lang="ts">
import { computed } from 'vue';
import { useSelectableList, type SelectableItem } from '../../libs/useSelectableList';

export interface TabItem extends SelectableItem<string> {
  label: string;
}

interface Props {
  items: TabItem[];
  modelValue: string;
  gap?: string;
  variant?: 'default' | 'slim';
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  gap: '0px',
  items: () => [],
  modelValue: '',
  variant: 'default' as 'default' | 'slim',
});
const emit = defineEmits<Emits>();

const model = computed<string>({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});

const { activeKey, select } = useSelectableList<string>({
  modelValue: model,
  items: computed(() => props.items),
  onChange: v => emit('change', v),
});

function onTabClick(item: TabItem) {
  select(item.key);
}
</script>

<template>
  <div class="tabs" :class="{ 'tabs--slim': variant === 'slim' }">
    <div class="tabs__list" role="tablist">
      <button
        v-for="item in items"
        :key="item.key"
        class="tabs__trigger"
        :class="{
          'tabs__trigger--active': activeKey === item.key,
          'tabs__trigger--disabled': item.disabled,
        }"
        role="tab"
        type="button"
        :aria-selected="activeKey === item.key"
        :aria-disabled="item.disabled || undefined"
        @click="onTabClick(item)"
      >
        <slot name="label" :item="item" :active="activeKey === item.key">
          {{ item.label }}
        </slot>
      </button>
    </div>

    <div class="tabs__content">
      <slot :active-key="activeKey" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/mixins.scss' as *;

.tabs__list {
  display: flex;
  justify-content: center;
  border-bottom: 1px solid var(--x-back-5);
  gap: v-bind(gap);

  @include mobile {
    gap: responsive-size(v-bind(gap));
  }
}

.tabs__trigger {
  position: relative;
  appearance: none;
  background: transparent;
  border: none;
  padding: 0px 20px 12px;
  color: var(--x-text-5);
  cursor: pointer;
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);

  @include mobile {
    padding: 0px responsive-size(20px) responsive-size(12px);
    font-size: responsive-size(var(--x-text-14-size));
    line-height: responsive-size(var(--x-text-14-line-height));
  }

  &:hover:not(.tabs__trigger--active) {
    color: var(--x-text-1);
    font-weight: var(--x-weight-regular);
  }
}

.tabs__trigger--active {
  color: var(--x-text-1);
  font-weight: var(--x-weight-medium);
}

.tabs__trigger--active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  background: var(--x-text-1);
}

.tabs__trigger--disabled {
  color: var(--x-text-6);
  cursor: not-allowed;
}

.tabs__content {
  padding-top: 12px;
  @include mobile {
    padding-top: responsive-size(12px);
  }
}

.tabs {
  width: 100%;
  &.tabs--slim {
    .tabs__list {
      display: flex;
      gap: 12px;
      border-bottom: 1px solid var(--x-back-4);
      justify-content: flex-start;
    }

    .tabs__trigger {
      position: relative;
      appearance: none;
      background: transparent;
      border: none;
      padding: 8px 0;
      color: var(--x-text-5);
      cursor: pointer;
      font-size: var(--x-text-16-size);
      line-height: var(--x-text-16-line-height);
    }

    .tabs__trigger--active {
      color: var(--x-text-1);
      font-weight: var(--x-weight-medium);
    }
  }
}
</style>
